class LazySection extends HTMLElement {
  static sectionsToFetch = [];

  constructor() {
    super();

    this.section =
      this.closest('.shopify-section')?.id ||
      this.closest('section')?.id ||
      this.closest('[id]')?.id;
    this.section = this.section?.replace('shopify-section-', '');

    this.trigger = this.dataset.triggerEvent || 'intersection-observer';
    this.targetSelector = this.dataset.triggerTarget || 'body';

    this.boundHandleMouseover = this.handleMouseover.bind(this);
    this.boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    if (!this.section || (window.Shopify && Shopify.designMode)) return;

    this.renderLoader();

    const targetElement = document.querySelector(this.targetSelector);

    if (this.trigger === 'mouseover') {
      if (screen.width < 990) return;
      targetElement?.addEventListener('mouseover', this.boundHandleMouseover);
    } else if (this.trigger === 'click') {
      targetElement?.addEventListener('click', this.boundHandleClick);
    } else {
      new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: '0px 0px 240px 0px',
      }).observe(this);
    }
  }

  renderLoader() {
    if (this.querySelector('.lazy-section-loader')) return;

    this.setAttribute('aria-busy', 'true');
    this.innerHTML = `
      <div class="lazy-section-loader" role="status" aria-label="Loading section">
        <div class="lazy-section-loader__header"></div>
        <div class="lazy-section-loader__media"></div>
        <div class="lazy-section-loader__row">
          <span></span><span></span><span></span><span></span>
        </div>
      </div>
    `;
  }

  markLoading() {
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.markLoading();
    this.fetchSections();
  }

  handleMouseover() {
    this.removeTrigger();
    this.markLoading();
    this.fetchSections();
  }

  handleClick() {
    this.removeTrigger();
    this.markLoading();
    this.fetchSections();
  }

  fetchSections() {
    if (!this.section || LazySection.sectionsToFetch.includes(this.section)) {
      if (LazySection.sectionsToFetch.length) this.requestBatch();
      return;
    }

    LazySection.sectionsToFetch.push(this.section);
    this.requestBatch();
  }

  requestBatch() {
    const sectionsToFetchBatch =
      LazySection.sectionsToFetch.length == 5
        ? LazySection.sectionsToFetch.splice(0, 5)
        : LazySection.sectionsToFetch;

    if (LazySection.abortController) {
      LazySection.abortController.abort();
    }

    LazySection.abortController =
      sectionsToFetchBatch.length < 5 ? new AbortController() : null;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionsToFetchBatch.join(','));

    fetch(
      url.pathname + url.search,
      LazySection.abortController
        ? { signal: LazySection.abortController.signal }
        : {}
    )
      .then((response) => response.json())
      .then((json) => {
        for (const [key, value] of Object.entries(json)) {
          const sectionContent = new DOMParser()
            .parseFromString(value, 'text/html')
            .getElementById('shopify-section-' + key);

          if (sectionContent && sectionContent.innerHTML.trim().length) {
            const section = document.getElementById('shopify-section-' + key);
            if (!section) continue;

            section.innerHTML = sectionContent.innerHTML;

            section.querySelectorAll('script').forEach((oldScriptTag) => {
              const newScriptTag = document.createElement('script');
              Array.from(oldScriptTag.attributes).forEach((attribute) => {
                newScriptTag.setAttribute(attribute.name, attribute.value);
              });
              newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
              oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
            });

            document.dispatchEvent(
              new CustomEvent('shopify:section:load', {
                bubbles: true,
                detail: { sectionId: key },
              })
            );

            document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
            if (window._swymSwat && typeof window._swymSwat.initializeActionButtons === 'function') {
              section.querySelectorAll('.wishlist-icon-product-card').forEach(function (container) {
                try {
                  window._swymSwat.initializeActionButtons(container);
                } catch (err) {}
              });
            }
          }
        }

        if (sectionsToFetchBatch.length < 5) LazySection.sectionsToFetch = [];
      })
      .catch((e) => {
        if (e && e.name === 'AbortError') return;
        console.warn(e);
      });
  }

  removeTrigger() {
    const targetElement = document.querySelector(this.targetSelector);
    if (targetElement) {
      targetElement.removeEventListener('mouseover', this.boundHandleMouseover);
      targetElement.removeEventListener('click', this.boundHandleClick);
    }
  }
}

if (!customElements.get('lazy-section')) {
  customElements.define('lazy-section', LazySection);
}
